import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { baseUrl } from "../../config/baseUrl";
import OwnerCouponForm from "./OwnerCouponForm";

const initialFormData = {
  code: "",
  discountType: "percentage",
  discountValue: "",
  validFrom: "",
  validTill: "",
  isActive: "true",
};

export default function OwnerCouponEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    const fetchCoupon = async () => {
      try {
        setFetchLoading(true);
        const response = await axios.get(`${baseUrl}/owner/coupons/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        const coupon = response.data.data;
        setFormData({
          code: coupon.code || "",
          discountType: coupon.discountType || "percentage",
          discountValue: coupon.discountValue || "",
          validFrom: coupon.validFrom?.split("T")[0] || "",
          validTill: coupon.validTill?.split("T")[0] || "",
          isActive: String(coupon.isActive),
        });
      } catch (error) {
        console.error("Fetch coupon error:", error);
        toast.error(error.response?.data?.message || "Failed to fetch coupon");
        navigate("/coupons");
      } finally {
        setFetchLoading(false);
      }
    };

    fetchCoupon();
  }, [id, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "code" ? value.toUpperCase() : value,
    }));
  };

  const validateForm = () => {
    if (!formData.code.trim()) {
      toast.error("Coupon code is required");
      return false;
    }

    if (!formData.discountValue || Number(formData.discountValue) <= 0) {
      toast.error("Discount value must be greater than 0");
      return false;
    }

    if (
      formData.discountType === "percentage" &&
      Number(formData.discountValue) > 100
    ) {
      toast.error("Percentage discount cannot exceed 100");
      return false;
    }

    if (!formData.validFrom || !formData.validTill) {
      toast.error("Please select valid dates");
      return false;
    }

    if (new Date(formData.validTill) <= new Date(formData.validFrom)) {
      toast.error("Valid till must be after valid from");
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.patch(
        `${baseUrl}/owner/coupons/${id}`,
        {
          code: formData.code.trim(),
          discountType: formData.discountType,
          discountValue: Number(formData.discountValue),
          validFrom: formData.validFrom,
          validTill: formData.validTill,
          isActive: formData.isActive === "true",
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.status) {
        toast.success("Coupon updated successfully");
        navigate("/coupons");
      }
    } catch (error) {
      console.error("Update coupon error:", error);
      toast.error(error.response?.data?.message || "Failed to update coupon");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <section className="main-sec">
        <div className="text-center py-5">
          <div className="spinner-border" role="status" />
          <p className="mt-2">Loading coupon details...</p>
        </div>
      </section>
    );
  }

  return (
    <OwnerCouponForm
      title="Edit Coupon"
      formData={formData}
      loading={loading}
      onChange={handleChange}
      onSubmit={handleSubmit}
      submitLabel="Update Coupon"
    />
  );
}
